import { Icon } from "@iconify/react";
import {
  Button,
  Card,
  Flex,
  Grid,
  Input,
  message,
  Pagination,
  Table,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import useTitleStore from "../../../store/titleStore";
import { useTableHeight } from "../../../hooks/useTableHeight";
import useDebounce from "../../../hooks/useDebounce";
import TableAction from "../../../components/TableAction";
import UpdatedByCell from "../../../components/UpdatedByCell";
import { programStudyService } from "../../../services/program-study.service";

export default function ProgramStudyIndex() {
  const breakpoint = Grid.useBreakpoint();

  const navigate = useNavigate();
  const { setTitle } = useTitleStore();
  const queryClient = useQueryClient();
  const tableHeight = useTableHeight();

  const [messageApi, contextHolder] = message.useMessage();
  const [keyword, setKeyword] = useState("");
  const debouncedKeyword = useDebounce(keyword, 500);
  const [dataParams, setDataParams] = useState({
    limit: 10,
    page: 1,
    keyword: null,
  });

  const {
    data: programStudy,
    isLoading,
    isRefetching,
  } = useQuery({
    queryKey: ["program-study", dataParams],
    queryFn: () => programStudyService.fetchAll(dataParams),
    placeholderData: keepPreviousData,
  });

  const { mutate: handleDelete, isPending: deleteLoading } = useMutation({
    mutationFn: (id) => programStudyService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries("program-study");
      messageApi.success("Data berhasil dihapus");
    },
    onError: (error) => {
      console.error(error);
      messageApi.error("Terjadi kesalahan saat menghapus data");
    },
  });

  useEffect(() => {
    setTitle("Master Data");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDataParams((prev) => ({
      ...prev,
      keyword: debouncedKeyword,
    }));
  }, [debouncedKeyword]);

  const handleChangePage = (page, pageSize) => {
    setDataParams({
      ...dataParams,
      limit: pageSize,
      page,
    });
  };

  const columns = [
    {
      title: "No",
      align: "center",
      width: 20,
      render: (text, record, index) =>
        (programStudy?.pagination.page - 1) * dataParams.limit + index + 1,
      fixed: breakpoint.md && "left",
    },
    {
      title: "Nama Program Studi",
      dataIndex: "name",
      width: 110,
      render: (val) => (
        <Typography.Text style={{ fontWeight: 600 }}>{val}</Typography.Text>
      ),
    },
    {
      title: "Update By",
      width: 100,
      render: (val) => <UpdatedByCell value={val} />,
    },
    {
      className: "last-cell-p",
      title: "Aksi",
      align: "right",
      width: 30,
      fixed: "right",
      render: (record) => (
        <TableAction
          onClickEdit={() =>
            navigate("/master-data/program-study/detail/" + record.uuid)
          }
          handleDelete={() => handleDelete(record.uuid)}
          deleteLoading={deleteLoading}
        />
      ),
    },
  ];

  return (
    <Card
      styles={{
        body: {
          paddingLeft: 0,
          paddingRight: 0,
        },
      }}
    >
      {contextHolder}
      <Flex
        justify="space-between"
        gap={20}
        style={{ paddingInline: 24, marginBottom: 24 }}
      >
        <Input
          style={{ width: 320 }}
          prefix={<Icon icon="hugeicons:search-01" width="16" height="16" />}
          placeholder="Cari"
          onChange={(e) => setKeyword(e.target.value)}
          value={keyword}
        />
        <Button
          variant="solid"
          color="primary"
          icon={<Icon icon="duo-icons:add-circle" width="22" height="22" />}
          onClick={() => navigate("/master-data/program-study/create")}
          style={{ minWidth: 38 }}
        >
          {breakpoint.md && "Tambah Program Studi"}
        </Button>
      </Flex>
      <Table
        columns={columns}
        pagination={false}
        size="small"
        loading={isLoading || isRefetching}
        rowKey={"id"}
        scroll={{ y: tableHeight, x: 1100 }}
        dataSource={programStudy?.data}
      />
      <Pagination
        style={{ marginInline: 24, marginTop: 16 }}
        total={programStudy?.pagination.total}
        showSizeChanger={{
          showSearch: false,
        }}
        pageSizeOptions={["10", "20", "50"]}
        current={programStudy?.pagination.page}
        pageSize={dataParams.limit}
        align="end"
        onChange={handleChangePage}
      />
    </Card>
  );
}
